import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import BookActivityPlaceholder from './BookActivityPlaceholder';
import ArtworkReactions from './ArtworkReactions';
import { getReactionArtworkId } from '../utils/reactionArtworkId';
import { syncArtworkVisibility } from '../utils/artworkVisibilitySync';
import { trackEvent, logError } from '../utils/observability';
import { API_ENDPOINTS } from '../config/backendConfig';
import {
  coLearningExpandedBackdropProps,
  coLearningInkBody,
  coLearningInkHeading,
  coLearningInkMuted,
  coLearningInkTitle,
  coLearningNestedBackdropProps,
  coLearningPromptBackdropProps,
  coLearningWallBackdropProps,
} from '../ui/coLearningModalTheme';
import { useArtistSalonAgeFilter } from '../context/ArtistSalonAgeFilterContext';

const BookWallModal = ({ book, activityCode, initialSubmissionId, onClose }) => {
  const { user } = useAuth();
  const { filterSubmissions } = useArtistSalonAgeFilter();
  const [submissions, setSubmissions] = useState([]);
  const visibleSubmissions = useMemo(
    () => filterSubmissions(submissions),
    [submissions, filterSubmissions]
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [artistNames, setArtistNames] = useState({});
  const currentIdRef = useRef(null);
  const initializedFromParamRef = useRef(false);
  const [showUnshareConfirm, setShowUnshareConfirm] = useState(false);
  const [taskPage, setTaskPage] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (user && book && activityCode) {
      loadSubmissions();
      loadArtistNames();
    }
  }, [user, book, activityCode]);

  useEffect(() => {
    if (visibleSubmissions.length > 0 && initialSubmissionId && !initializedFromParamRef.current) {
      const index = visibleSubmissions.findIndex(sub => sub.id === initialSubmissionId);
      if (index !== -1) {
        setCurrentIndex(index);
        currentIdRef.current = initialSubmissionId;
        initializedFromParamRef.current = true;
      }
    }
  }, [visibleSubmissions, initialSubmissionId]);

  // Preserve current artwork when submissions array updates
  useEffect(() => {
    if (visibleSubmissions.length > 0 && currentIdRef.current && initializedFromParamRef.current) {
      const newIndex = visibleSubmissions.findIndex(sub => sub.id === currentIdRef.current);
      if (newIndex !== -1 && newIndex !== currentIndex) {
        setCurrentIndex(newIndex);
      }
    }
  }, [visibleSubmissions, currentIndex]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      
      const submissionsQuery = query(
        collection(db, 'bookArtworks'),
        where('book', '==', book),
        where('activityCode', '==', activityCode),
        where('visibility', '==', 'public'),
        orderBy('createdAt', 'desc')
      );
      
      const submissionsSnapshot = await getDocs(submissionsQuery);
      const allDocs = submissionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const taskSeed = allDocs.find((item) => !!item.isSystemSeed || item.uid === 'system-book-seed') || null;
      const submissionsData = allDocs.filter((item) => !(!!item.isSystemSeed || item.uid === 'system-book-seed'));
      setTaskPage(taskSeed);

      // Sort by max reactions first, then latest contribution (createdAt desc)
      // We interpret "max reactions" as total kudos from the /api/kudos/count endpoint.
      try {
        const totals = await Promise.all(
          submissionsData.map(async (submission) => {
            const reactionArtworkId = getReactionArtworkId(submission);
            if (!reactionArtworkId) return [submission.id, 0];
            try {
              const resp = await fetch(`${API_ENDPOINTS.getKudosCount}?artworkId=${encodeURIComponent(reactionArtworkId)}`);
              const data = await resp.json();
              if (data?.success) return [submission.id, data?.totalReactions ?? 0];
              return [submission.id, 0];
            } catch (_) {
              return [submission.id, 0];
            }
          })
        );
        const reactionMap = {};
        totals.forEach(([id, total]) => { reactionMap[id] = total; });

        const toMillis = (t) => {
          try {
            if (!t) return 0;
            if (typeof t?.toMillis === 'function') return t.toMillis();
            if (t?._seconds) return t._seconds * 1000 + (t._nanoseconds || 0) / 1000000;
            if (t instanceof Date) return t.getTime();
            const d = new Date(t);
            return isNaN(d.getTime()) ? 0 : d.getTime();
          } catch (_) { return 0; }
        };

        submissionsData.sort((a, b) => {
          const ra = reactionMap[a?.id] ?? 0;
          const rb = reactionMap[b?.id] ?? 0;
          if (rb !== ra) return rb - ra;
          return toMillis(b?.createdAt) - toMillis(a?.createdAt);
        });
      } catch (e) {
        console.warn('[BookWallModal] Failed to sort submissions by reactions:', e);
        // Fall back to createdAt ordering
      }

      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadArtistNames = async () => {
    try {
      const artistProfilesSnapshot = await getDocs(collection(db, 'artistProfiles'));
      const names = {};
      artistProfilesSnapshot.forEach((doc) => {
        const data = doc.data();
        names[data.uid] = data.name;
      });
      setArtistNames(names);
    } catch (error) {
      console.error('Error loading artist names:', error);
    }
  };

  const nextSubmission = () => {
    if (currentIndex < visibleSubmissions.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      currentIdRef.current = visibleSubmissions[newIndex].id;
    }
  };

  const prevSubmission = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      currentIdRef.current = visibleSubmissions[newIndex].id;
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showUnshareConfirm) {
          setShowUnshareConfirm(false);
          return;
        }
        if (isExpanded) {
          setIsExpanded(false);
          return;
        }
        onClose();
      } else if (e.key === 'ArrowLeft') {
        prevSubmission();
      } else if (e.key === 'ArrowRight') {
        nextSubmission();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, visibleSubmissions.length, showUnshareConfirm, isExpanded, onClose]);

  if (!loading && visibleSubmissions.length === 0) {
    const empty = (
      <div {...coLearningPromptBackdropProps} onClick={onClose}>
        <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-8" onClick={(e) => e.stopPropagation()}>
          <div className="text-center">
            <div className="text-6xl mb-4">📚</div>
            <h3 className={`text-xl mb-2 ${coLearningInkTitle}`}>
              No Artworks Yet
            </h3>
            <p className={`mb-6 ${coLearningInkBody}`}>
              No artworks have been shared for this book activity yet.
            </p>
            <button
              onClick={onClose}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2 px-6 rounded-full"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(empty, document.body) : empty;
  }

  const currentSubmission = visibleSubmissions[currentIndex] || visibleSubmissions[0] || null;
  const artistName = currentSubmission ? (artistNames[currentSubmission.uid] || 'Unknown Artist') : 'Unknown Artist';
  const isOwner = !!user && currentSubmission?.uid === user.uid;

  // Keep loading silent and non-blocking; avoid dereferencing until data exists.
  if (!currentSubmission) {
    return null;
  }

  const modalTree = (
    <>
    <div {...coLearningWallBackdropProps} onClick={onClose}>
      <div
        className="relative flex h-[min(96vh,calc(100dvh-1rem))] w-full max-w-[min(92rem,calc(100vw-1.25rem))] flex-col overflow-hidden rounded-2xl border border-[#e8e0d4] bg-white shadow-xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Compact header — activity dashboard style (no amber) */}
        <div className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-[#e8e0d4]/80 shadow-sm max-h-[72px] md:max-h-[80px]">
          <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px] px-4 md:px-6">
            <h3 className={`text-sm md:text-base truncate pr-2 ${coLearningInkHeading}`}>{book?.title || book?.name || 'Book Co-Learning'}</h3>
            <button onClick={onClose} className="p-1.5 md:p-2 rounded-full bg-stone-100/90 text-[#141210] hover:bg-stone-200/90 transition-colors flex-shrink-0" aria-label="Close">
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation Arrows */}
        {visibleSubmissions.length > 1 && (
          <>
            <button
              onClick={prevSubmission}
              disabled={currentIndex === 0}
              className="absolute left-2 top-[42%] z-10 hidden rounded-full bg-white/85 p-2.5 text-[#141210] backdrop-blur-sm hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 md:left-4 md:top-1/2 md:block md:-translate-y-1/2 md:p-3"
            >
              <FaChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextSubmission}
              disabled={currentIndex === visibleSubmissions.length - 1}
              className="absolute right-2 top-[42%] z-10 hidden rounded-full bg-white/85 p-2.5 text-[#141210] backdrop-blur-sm hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 md:right-4 md:top-1/2 md:block md:-translate-y-1/2 md:p-3"
            >
              <FaChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
          {/* Activity task page — full width on mobile, half on desktop */}
          <div className="flex min-h-0 flex-[1.05] flex-col overflow-hidden border-b border-[#e8e0d4] bg-white md:w-1/2 md:border-b-0 md:border-r">
            <BookActivityPlaceholder
              book={book}
              activityCode={activityCode}
              imageUrl={taskPage?.imageUrl || taskPage?.artworkUrl || taskPage?.bookImage || null}
            />
          </div>

          {/* Artwork — stacked below activity on mobile */}
          <div className="relative flex min-h-0 flex-1 items-stretch justify-center bg-[#f5f2eb] md:w-1/2">
            {currentSubmission?.imageUrl ? (
              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className="flex h-full min-h-0 w-full flex-1 items-center justify-center p-2 md:p-3"
                title="Click to expand artwork"
              >
                <img
                  src={currentSubmission.imageUrl}
                  alt={currentSubmission.title}
                  loading="eager"
                  decoding="sync"
                  fetchPriority="high"
                  className="max-h-full max-w-full h-full w-full object-contain"
                />
              </button>
            ) : null}
            
            {/* (Counts bubble removed in favor of unified kid‑friendly reactions component) */}
          </div>
        </div>

          {/* Details Section - Compact Overlay */}
          <div className="bg-white/95 backdrop-blur-sm p-4 border-t border-[#e8e0d4] flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className={`text-lg mb-1 ${coLearningInkTitle}`}>
                  {currentSubmission.title}
                </h2>
                <p className={`text-sm mb-2 ${coLearningInkBody}`}>
                  by <span className="font-medium text-[#141210]">{artistName}</span>
                </p>
                {currentSubmission.technique && (
                  <p className={`text-xs ${coLearningInkMuted}`}>
                    <strong className="text-[#141210]">Technique:</strong> {currentSubmission.technique}
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-4">
                {/* Unified positive‑only GIF reactions, consistent with Artist Wall & Co‑Learning */}
                <div className="flex items-center gap-3">
                  <ArtworkReactions
                    artworkId={getReactionArtworkId(currentSubmission)}
                    artistId={currentSubmission.uid}
                    enableComments={false}
                  />
                </div>

                {/* Owner unshare button */}
                {isOwner && (
                  <button
                    onClick={() => setShowUnshareConfirm(true)}
                    className="px-3 py-1 rounded-full bg-white text-[#141210] border border-[#e0d8cc] hover:bg-stone-50 text-xs"
                  >
                    Unshare
                  </button>
                )}

                {/* Navigation Info */}
                {visibleSubmissions.length > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={prevSubmission}
                      disabled={currentIndex === 0}
                      className="rounded-full border border-[#e0d8cc] bg-white px-2 py-1 text-xs font-medium text-[#141210] disabled:opacity-40 md:hidden"
                      aria-label="Previous artwork"
                    >
                      Prev
                    </button>
                    <div className={`text-sm ${coLearningInkMuted}`}>
                      {currentIndex + 1} of {visibleSubmissions.length}
                    </div>
                    <button
                      type="button"
                      onClick={nextSubmission}
                      disabled={currentIndex === visibleSubmissions.length - 1}
                      className="rounded-full border border-[#e0d8cc] bg-white px-2 py-1 text-xs font-medium text-[#141210] disabled:opacity-40 md:hidden"
                      aria-label="Next artwork"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    {/* Expanded artwork view */}
    {isExpanded && currentSubmission?.imageUrl && (
      <div
        role="presentation"
        {...coLearningExpandedBackdropProps}
        onClick={() => setIsExpanded(false)}
      >
        <div
          className="relative z-[1] mx-auto flex h-full w-full max-w-7xl items-center justify-center gap-2 px-1 sm:gap-4 sm:px-3"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="absolute right-3 top-3 z-[510] flex h-11 w-11 items-center justify-center rounded-full text-[#141210] shadow-[0_8px_28px_rgba(0,0,0,0.12)] transition-transform hover:scale-105 sm:right-5 sm:top-5 sm:h-12 sm:w-12"
            style={{
              background: 'rgba(255,255,255,0.88)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}
            aria-label="Close expanded view"
          >
            <FaTimes className="h-5 w-5" />
          </button>
          {visibleSubmissions.length > 1 && (
            <button
              type="button"
              onClick={prevSubmission}
              disabled={currentIndex === 0}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/55 bg-white/[0.72] text-[#141210] shadow-[0_4px_20px_rgba(0,0,0,0.12)] backdrop-blur-md transition-all hover:scale-[1.08] active:scale-95 disabled:pointer-events-none disabled:opacity-35 sm:h-12 sm:w-12"
              style={{ touchAction: 'manipulation', WebkitBackdropFilter: 'blur(12px)' }}
              aria-label="Previous submission"
            >
              <FaChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          )}
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center pb-[4.5rem] sm:pb-20">
            <img
              src={currentSubmission.imageUrl}
              alt="Expanded submission"
              className="max-h-[min(78dvh,88vh,920px)] max-w-full object-contain drop-shadow-2xl"
            />
            <div className="pointer-events-none absolute bottom-3 left-0 right-0 z-[510] flex justify-center sm:bottom-5">
              <div
                className="pointer-events-auto max-w-[min(100%,20rem)] rounded-2xl px-4 py-2 text-center text-xs font-medium text-[#141210] ring-1 ring-white/40 sm:max-w-none sm:px-5 sm:py-2.5 sm:text-sm"
                style={{
                  background: 'rgba(255,255,255,0.88)',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
                }}
              >
                {currentIndex + 1} of {visibleSubmissions.length} · {artistName}
              </div>
            </div>
          </div>
          {visibleSubmissions.length > 1 && (
            <button
              type="button"
              onClick={nextSubmission}
              disabled={currentIndex === visibleSubmissions.length - 1}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/55 bg-white/[0.72] text-[#141210] shadow-[0_4px_20px_rgba(0,0,0,0.12)] backdrop-blur-md transition-all hover:scale-[1.08] active:scale-95 disabled:pointer-events-none disabled:opacity-35 sm:h-12 sm:w-12"
              style={{ touchAction: 'manipulation', WebkitBackdropFilter: 'blur(12px)' }}
              aria-label="Next submission"
            >
              <FaChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          )}
        </div>
      </div>
    )}
    {/* Unshare confirm modal */}
    {showUnshareConfirm && (
      <div {...coLearningNestedBackdropProps}>
        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl border border-[#e8e0d4]">
          <div className="px-6 py-5">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-stone-200 to-stone-300 text-[#141210] font-bold shadow mb-3">A</div>
            <h3 className={`text-xl mb-2 ${coLearningInkTitle}`}>Unshare from Co‑Learning?</h3>
            <p className={`mb-4 text-sm ${coLearningInkBody}`}>Unsharing will lock this activity again for you and you may not see new works from other artists. We encourage staying public to promote co‑learning, but the choice is yours.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowUnshareConfirm(false)} className={`px-4 py-2 rounded-full border border-[#e0d8cc] bg-white hover:bg-stone-50 ${coLearningInkBody}`}>Keep Public</button>
              <button onClick={async () => {
                try {
                  const oldVisibility = currentSubmission.visibility || 'public';
                  await updateDoc(doc(db, 'bookArtworks', currentSubmission.id), { visibility: 'private', updatedAt: serverTimestamp() });
                  trackEvent('book_artwork_unshared', {
                    artworkId: currentSubmission.id,
                    userId: user?.uid || null,
                  });
                  
                  // Sync with co-learning space (remove from public view)
                  await syncArtworkVisibility(
                    { ...currentSubmission, visibility: 'private' },
                    'private',
                    oldVisibility,
                    'bookArtworks'
                  );
                  
                  const updated = submissions.filter(s => s.id !== currentSubmission.id);
                  setSubmissions(updated);
                  setCurrentIndex(0);
                  setShowUnshareConfirm(false);
                  // Also decrement the count in parent activity list by filtering visibility when reloaded
                  // This modal owns its own count view; updated length reflects new count
                  if (updated.length === 0) onClose();
                } catch (e) {
                  console.error('Failed to unshare book artwork:', e);
                }
              }} className="px-4 py-2 rounded-full text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">Unshare</button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );

  return typeof document !== 'undefined' ? createPortal(modalTree, document.body) : modalTree;
};

export default BookWallModal;
